import { db } from '../db';
import { createId } from '../lib/id';
import { photoForStorage } from '../lib/image';
import { MAX_ITEM_TEXT_LENGTH, normalizeItemValues } from '../lib/item-values';
import { renamedPlaceValues, type PlaceKind } from '../lib/places';
import type { ItemRecord, ItemStatus, SerializableItem } from '../types';

export type { PlaceKind } from '../lib/places';

export async function createDraft(photo: Blob) {
  const now = new Date().toISOString();
  const item: ItemRecord = {
    id: createId('item'),
    name: '',
    locationNote: '',
    locationGroup: '',
    photo,
    photoMimeType: photo.type || 'image/jpeg',
    synonyms: [],
    aiSuggestions: [],
    status: 'needs_name',
    createdAt: now,
    updatedAt: now,
    pendingSync: false
  };
  const storedItem: ItemRecord = { ...item, photo: await photoForStorage(photo) };
  await db.items.add(storedItem);
  void requestDurableStorage();
  return storedItem;
}

export async function saveItem(item: ItemRecord, values: { name: string; locationNote: string; locationGroup?: string; synonyms?: string[] }) {
  const { name, locationNote, locationGroup, synonyms } = normalizeItemValues({ ...values, synonyms: values.synonyms ?? item.synonyms });
  const status: ItemStatus = locationNote ? 'ready' : 'needs_location';
  const updated: ItemRecord = {
    ...item,
    name,
    locationNote,
    locationGroup,
    synonyms,
    aiSuggestions: [],
    status,
    updatedAt: new Date().toISOString(),
    pendingSync: true
  };
  const storedPhoto = await photoForStorage(updated.photo);

  await db.transaction('rw', db.items, db.outbox, async () => {
    await db.items.put({ ...updated, photo: storedPhoto });
    await queueUpsert(updated);
  });
  return updated;
}

export async function applyBackgroundPatch(itemId: string, expectedUpdatedAt: string, patch: Partial<Pick<ItemRecord, 'aiSuggestions' | 'status'>>) {
  const snapshot = await db.items.get(itemId);
  if (!snapshot || snapshot.updatedAt !== expectedUpdatedAt) return null;
  const storedPhoto = await photoForStorage(snapshot.photo);

  return db.transaction('rw', db.items, db.outbox, async () => {
    const current = await db.items.get(itemId);
    if (!current || current.updatedAt !== expectedUpdatedAt) return null;
    const updated: ItemRecord = {
      ...current,
      ...patch,
      updatedAt: new Date().toISOString(),
      pendingSync: true
    };
    await db.items.put({ ...updated, photo: storedPhoto });
    await queueUpsert(updated);
    return updated;
  });
}

export async function renamePlace(kind: PlaceKind, previousName: string, nextName: string) {
  const previous = previousName.trim();
  const next = nextName.trim();
  if (!previous || !next) throw new Error('Enter a place name before saving.');
  if (next.length > MAX_ITEM_TEXT_LENGTH) throw new Error(`Keep the place name under ${MAX_ITEM_TEXT_LENGTH} characters.`);
  if (previous === next) return 0;

  const allItems = await db.items.toArray();
  const matches = allItems.flatMap((item) => {
    const values = renamedPlaceValues(item, kind, previous, next);
    return values ? [{ item, values }] : [];
  });
  if (!matches.length) return 0;

  const updatedAt = new Date().toISOString();
  await db.transaction('rw', db.items, db.outbox, async () => {
    for (const { item, values } of matches) {
      const updated: ItemRecord = {
        ...item,
        ...values,
        updatedAt,
        pendingSync: true
      };
      await db.items.put(updated);
      await queueUpsert(updated);
    }
  });
  return matches.length;
}

export async function deleteItem(item: ItemRecord) {
  const updatedAt = new Date().toISOString();
  await db.transaction('rw', db.items, db.outbox, async () => {
    await db.items.delete(item.id);
    await db.outbox.put({
      itemId: item.id,
      id: createId('event'),
      type: 'item.delete',
      payload: { id: item.id, updatedAt },
      createdAt: updatedAt
    });
  });
}

export async function clearLocalData() {
  await db.transaction('rw', db.items, db.outbox, db.settings, async () => {
    await Promise.all([db.items.clear(), db.outbox.clear(), db.settings.clear()]);
  });
}

function serializableItem(item: ItemRecord): SerializableItem {
  const { photo: _photo, ...serializable } = item;
  return serializable;
}

async function queueUpsert(item: ItemRecord) {
  await db.outbox.put({
    itemId: item.id,
    id: createId('event'),
    type: 'item.upsert',
    payload: serializableItem(item),
    createdAt: item.updatedAt
  });
}

async function requestDurableStorage() {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Storage persistence is an enhancement; IndexedDB remains the source of truth.
  }
}
